<script setup lang="ts">
import { rewardsPopup } from '@/components/games/rewardsPopup/RewardsPopup.ts'
import { offerDialog } from '@/components/shared/OfferDialog/OfferDialogCompose'
import { gamesUtils } from '@/compositions/gamesUtils'

const { routeGame, listById, logOut, triggerApi, goadLoadAliveData } = gamesUtils
const { getLocalePath } = tools
const route = useRoute()
const router = useRouter()
const { gaSections } = gaUtils

let isMounted = false
const { debounce } = tools

const offers = ref<{ [key in Games.GameKeyType]?: Array<Games.Offer> }>({})
const freeOffers = ref<Array<Games.Offer>>([])
const freeOffersNotClaimed = computed(() => {
  let visible = false
  freeOffers.value.some((offer) => {
    if (!offer.isBought) {
      visible = true
      return true
    }
    return false
  })
  return visible
})
const loyaltyLeagues = ref<{ [key in Games.GameKeyType]?: Games.LoyaltyLeagues[] }>({})
const loyaltyLeagueRewards = ref<{ [key in Games.GameKeyType]?: Record<string, Games.LoyaltyLeaguesRewards[]> }>({})
const loyaltyLeaguesPlayer = ref<{ [key in Games.GameKeyType]?: Games.LoyaltyLeaguesPlayer }>({})
const loyaltySeason = ref<{ [key in Games.GameKeyType]?: Games.LoyaltySeason | null }>({})
const loyaltySeasonMilestones = ref<{ [key in Games.GameKeyType]?: Games.LoyaltySeasonMilestone[] }>({})
const seasonEndsAtMs = ref<{ [key in Games.GameKeyType]?: number | null }>({})
const loaded = ref<{ [key in Games.GameKeyType]?: boolean }>({})
function loadGame() {
  const gameKey = routeGame.value?.gameKey
  if (!isMounted) {
    return
  }
  if (gameKey && routeGame.value?.active) {
    gamesUtils
      .apiGet(gameKey, 'offers/game/')
      .then(async ({ data }) => {
        if (!isMounted) {
          return
        }

        // Start: TODO:
        if (route.query?.tt) {
          const { applyTestData } = await import('@/devtools/testsLoader.ts')
          applyTestData(data, route.query as Record<string, string>)
        }
        // END: TODO:

        if ((data?.loyaltyRewardsMerged?.items?.length || data?.loyaltyRewardsMerged?.products?.length) && !rewardsPopup.value.visible) {
          rewardsPopup.value.rewardsApi[gameKey] = data.loyaltyRewardsMerged
          rewardsPopup.value.loyaltyLeaguesPlayer[gameKey] = { collectedRewards: 0, league: '', leaguePoints: 0, ...(data?.loyaltyLeaguesPlayer ?? {}) /* , ...{ collectedRewards: 10 } */ }
          rewardsPopup.value.loyaltyLeagueRewards[gameKey] = data?.loyaltyLeagueRewards ?? {}
          rewardsPopup.value.loyaltyCurrentLeagues[gameKey] = data?.loyaltyLeagues ?? {}
          rewardsPopup.value.visible = true
        }
        loyaltyLeagueRewards.value[gameKey] = data?.loyaltyLeagueRewards ?? {}
        loyaltyLeaguesPlayer.value[gameKey] = { collectedRewards: 0, league: '', leaguePoints: 0, ...(data?.loyaltyLeaguesPlayer ?? {}) /* , ...{ collectedRewards: 10 } */ }
        // set as last because of trigger watcher
        loyaltyLeagues.value[gameKey] = (data?.loyaltyLeagues ?? []).sort((a: Games.LoyaltyLeagues, b: Games.LoyaltyLeagues) => a.no - b.no).slice(0, 6)
        loyaltySeason.value[gameKey] = data?.loyaltySeason ?? null
        loyaltySeasonMilestones.value[gameKey] = data?.loyaltySeasonMilestones ?? []
        seasonEndsAtMs.value[gameKey] = data?.loyaltySeason?.endsAt ? Math.max(0, data.loyaltySeason.endsAt * 1000 - Date.now()) : null
        const storeUser: Games.Lax<Games.ApiLoyaltyData> = data?.storeUser || null
        if (storeUser) {
          storeUser.leaguePoints = loyaltyLeaguesPlayer.value[gameKey]?.leaguePoints || 0
          storeUser.leagueName = loyaltyLeaguesPlayer.value[gameKey]?.league || ''
          let leagueLogo = (loyaltyLeagues.value[gameKey] || []).find(item => item.name === storeUser.leagueName)?.assetLogo || ''
          if (leagueLogo && !leagueLogo.startsWith('http')) {
            leagueLogo = `${gamesUtils.list[gameKey]?.imgStorageUrl || ''}${leagueLogo}`
          }
          storeUser.leagueAssetLogo = leagueLogo
        }
        gamesUtils.setLoyalty(gameKey, storeUser)
        offers.value = {
          [gameKey]: data.offers
            ? gamesUtils.renderApiOffers(
              data.offers,
              gameKey,
              {
                gs_game: gameKey,
                gs_route: String(route.name),
                gs_section: gaSections.offersList
              }
            )
              .offers
            : []
        }
        freeOffers.value = data.free
          ? gamesUtils.renderApiOffers(
            data.free,
            gameKey,
            {
              gs_game: gameKey,
              gs_route: String(route.name),
              gs_section: gaSections.free
            }
          )
            .offers
          : []
        gamesUtils.checkCart([...(offers.value[gameKey] ?? [])], gameKey)
      })
      .catch((err) => {
        if (err?.response?.status === 401 || err?.status === 401) {
          logOut(gameKey, err)
        } else {
          gamesUtils.catchError(err)
        }
      })
      .then(() => {
        loaded.value[gameKey] = true
      })
  }
}
const loadGameDebounced = debounce(() => {
  // DEBUG disable it;
  goadLoadAliveData()
  loadGame()
}, 500)
onMounted(() => {
  if (!listById?.[String(route?.params?.gameID)] || routeGame.value?.active === false) {
    router.push(getLocalePath('home-page'))
  }
  isMounted = true
  goadLoadAliveData()
  loadGame()
  watch(triggerApi.triggerRef, () => {
    if (!offerDialog.value.visible && !rewardsPopup.value.visible) {
      loadGameDebounced()
    }
  })
})
onUnmounted(() => {
  isMounted = false
})
</script>

<template>
  <el-progress
    v-if="!loaded?.[routeGame?.gameKey || '']"
    :percentage="100"
    :indeterminate="true"
    :show-text="false"
    class="fixed left-0 right-0 top-0 z-999999"
  />
  <div
    class=""
    :class="{
      'blur-md animate-[ping-mini_2s_linear_infinite]': !loaded?.[routeGame?.gameKey || '']
    }"
  >
    <LoyaltyGameBoxComponent
      :loyalty-leagues="loyaltyLeagues"
      :loyalty-league-rewards="loyaltyLeagueRewards"
      :loyalty-leagues-player="loyaltyLeaguesPlayer"
    />
    <LoyaltyGameMilestonesBoxComponentAuto
      v-if="routeGame?.isEnabledMilestones"
      :loyalty-season="loyaltySeason"
      :loyalty-season-milestones="loyaltySeasonMilestones"
      :season-ends-at-ms="seasonEndsAtMs"
      :disable-sticky-variant="false"
    />
    <!-- "add to home screen" section right under the milestones (under the loyalty box when a game has no milestones) -->
    <install-btn-component />
    <game-category-offers-component
      :offers="offers"
      :free-offers="freeOffersNotClaimed"
    >
      <template #free>
        <div
          id="freeBox"
          class="h-0 -translate-y-[100px]"
        />
        <free-item-component
          :free="freeOffers"
          class="my-4 z-10 relative"
        />
      </template>
    </game-category-offers-component>
  </div>
</template>

<style scoped lang="postcss"></style>
